import React, { useState, useEffect, useRef } from 'react';
import BBITable from '../../../helpers/bBITable/BBITable';
import classesGlobal from '../../../assets/Global.module.css';
import classesFinancialReport from './FinancialReport.module.css';
import Filter from '../../../helpers/filter/Filter';
import FilterData from '../../../helpers/filter/FilterData';
import Get from '../../../api/internal/Get';
import Header from '../../../layouts/Header';
import View from '../../../helpers/slab/View';
import ViewLoad from '../../../features/views/load/Load';
import classesCustom from '../../../helpers/search/CustomSearch.module.css';
import { MdDownload } from 'react-icons/md';
import {
  GenerateQueryString,
  ParseUrlForSearchParams,
  GetLastFullWeekMonday,
} from '../../../../src/helpers/search/HelperFunctions';
import Input from '../../../assets/essentials/Input';
import Button from '../../../assets/essentials/Button';
import Select from '../../../assets/essentials/Select';
import OptionList from '../../../helpers/options/OptionList';
import PostBody from '../../../api/internal/PostBody';
import DateOnlyFormatter from '../../../helpers/inputs/DateOnlyFormatter';
import { useOutletContext } from 'react-router-dom';

function BrokerFinancials({ toggleBoardRefresh }) {
  const [isLoading, setIsLoading] = useState<boolean>();
  const [brokerFinancialsBoard, setBrokerFinancialsBoard] = useState<
    Array<BrokerFinancialsBoard>
  >([]);
  const emptySearchParams = {
    loadId: null,
    reference: null,
    branchId: null,
    customerId: null,
    dateMin: null, //must use name "dateMin" if we want to use defaultSearchDate
    dateMax: null,
  };
  const [brokerFinancialSearchParams, setBrokerFinancialSearchParams] =
    useState(emptySearchParams);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [branchOptions, setBranchOptions] = useState([]);
  const isFirstRender = useRef(true);
  const filterValue = useOutletContext();

  useEffect(() => {
    setIsLoading(true);
    if (customerOptions.length === 0) {
      GetCustomerOptions();
    }
    if (branchOptions.length === 0) {
      GetBranchList();
    }

    const { loadId, reference, branchId, customerId, dateMin, dateMax } =
      ParseUrlForSearchParams(
        emptySearchParams,
        GetLastFullWeekMonday(),
        isFirstRender.current,
      );

    setBrokerFinancialSearchParams({
      loadId: loadId,
      reference: reference,
      branchId: branchId,
      customerId: customerId,
      dateMin: dateMin,
      dateMax: dateMax,
    });
    SearchBrokerFinancialReport({
      loadId: loadId,
      reference: reference,
      branchId: branchId,
      customerId: customerId,
      dateMin: dateMin,
      dateMax: dateMax,
    });
    isFirstRender.current = false;
  }, [toggleBoardRefresh]);

  function SearchBrokerFinancialReport(SearchParamsFromForm?: Object) {
    const searchParams = SearchParamsFromForm
      ? SearchParamsFromForm
      : brokerFinancialSearchParams;
    const queryString = '?' + GenerateQueryString(searchParams);
    const newUrl =
      window.location.origin + window.location.pathname + queryString;
    window.history.replaceState(null, null, newUrl);
    Get(`/Accounting/SearchBrokerFinancialReport${queryString}`).then(
      response => {
        if (response) {
          setBrokerFinancialsBoard(response.data);
          setIsLoading(false);
        }
      },
    );
  }

  function GetCustomerOptions() {
    Get(`/Customer/GetCustomerLoadReportOptions/`).then(response => {
      if (response) {
        setCustomerOptions(response?.data);
      }
    });
  }
  function GetBranchList() {
    Get(`/Load/GetBranchList`).then(response => {
      if (response) {
        setBranchOptions(response.data);
      }
    });
  }

  function ClearSearchParams() {
    setBrokerFinancialSearchParams(emptySearchParams);
  }

  function HandleFormSubmit(e) {
    e.preventDefault();
    setIsLoading(true);
    const formData = {
      loadId: e.target.loadId?.value,
      reference: e.target.reference?.value,
      branchId: e.target.branchId?.value,
      customerId: e.target.customerId?.value,
      dateMin: e.target.dateMin?.value,
      dateMax: e.target.dateMax?.value,
    };
    setBrokerFinancialSearchParams(formData);
    SearchBrokerFinancialReport(formData);
  }

  function DownloadReportCSV(loads) {
    const loadIds = loads?.map(x => x.loadId);
    if (loadIds) {
      setIsLoading(true);
      PostBody('/Accounting/DownloadBrokerFinancialReportCSV', {
        loadIds: [...loadIds],
      }).then(response => {
        if (response) {
          const blob = new Blob([response.data], { type: 'text/csv' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download =
            new Date().toISOString().split('T')[0].replace(/[-]/g, '') +
            '_Broker Financials Report.csv';
          a.click();
          URL.revokeObjectURL(url);
          setIsLoading(false);
        }
      });
    }
  }

  const formattedBoard = brokerFinancialsBoard.map(brokerFinancial => ({
    ...brokerFinancial,
    pickupDate: DateOnlyFormatter(brokerFinancial.pickupDate),
    dropOffDate: DateOnlyFormatter(brokerFinancial.dropOffDate),
    miles: brokerFinancial.miles.toLocaleString('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 1,
      }),
    invoiceDate: DateOnlyFormatter(brokerFinancial.invoiceDate),
    revenue: brokerFinancial.revenue.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
    }),
    payroll: brokerFinancial.payroll.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
    }),
    payable: brokerFinancial.payable.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
    }),
    margin: brokerFinancial.margin.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
    }),
    marginPercentage: (brokerFinancial.marginPercentage).toFixed(1),
  }));

  const columnConfig = [
    {
      key: '1',
      attribute: 'loadId',
      header: 'Load #',
      onClick: e => View(<ViewLoad loadId={e.target.innerText} />),
      dataType: 'number',
    },
    {
      key: '2',
      attribute: 'loadStatus',
      header: 'Load Status',
    },
    {
      key: '3',
      attribute: 'reference',
      header: 'Reference',
    },
    {
      key: '4',
      attribute: 'branchName',
      header: 'Branch',
    },
    {
      key: '5',
      attribute: 'pickupDate',
      header: 'Pickup Date',
      dataType: 'date',
    },
    {
      key: '6',
      attribute: 'dropOffDate',
      header: 'Drop Off Date',
      dataType: 'date',
    },
    {
      key: '7',
      attribute: 'miles',
      header: 'Miles',
      dataType: 'formattedNumber',
      includeInTotal: true,
    },
    {
      key: '8',
      attribute: 'customerNames',
      header: 'Customer',
    },
    {
      key: '9',
      attribute: 'vendorNames',
      header: 'Vendor',
    },
    {
      key: '10',
      attribute: 'invoiceDate',
      header: 'Invoice Date',
      dataType: 'date',
    },
    {
      key: '11',
      attribute: 'revenue',
      header: 'Revenue',
      dataType: 'currency',
      includeInTotal: true,
    },
    {
      key: '12',
      attribute: 'payroll',
      header: 'Payroll',
      dataType: 'currency',
      includeInTotal: true,
    },
    {
      key: '13',
      attribute: 'payable',
      header: 'Payable',
      dataType: 'currency',
      includeInTotal: true,
    },
    {
      key: '14',
      attribute: 'margin',
      header: 'Margin',
      dataType: 'currency',
      includeInTotal: true,
    },
    {
      key: '15',
      attribute: 'marginPercentage',
      header: 'Margin %',
      dataType: 'number',
      includeInTotal: true,
    },
  ];

  let tableData = FilterData(formattedBoard, filterValue);

  const SearchForm = () => {
    return (
      <div className={classesCustom.customSearch}>
        <form onSubmit={e => HandleFormSubmit(e)}>
          <label>
            Load #
            <Input
              type="text"
              name="loadId"
              defaultValue={brokerFinancialSearchParams.loadId}
            />
          </label>
          <label>
            Reference #
            <Input
              type="text"
              name="reference"
              defaultValue={brokerFinancialSearchParams.reference}
            />
          </label>
          <label>
            Branch
            <Select
              defaultValue={brokerFinancialSearchParams.branchId || ''}
              name="branchId"
            >
              <option value="">All Branches</option>
              <OptionList
                optionData={branchOptions}
                attributeID="branchId"
                attributeName="branchName"
              />
            </Select>
          </label>
          <label>
            Customer
            <Select
              defaultValue={brokerFinancialSearchParams.customerId || ''}
              name="customerId"
            >
              <option value="">All Customers</option>
              <OptionList
                optionData={customerOptions}
                attributeID="customerId"
                attributeName="customerName"
              />
            </Select>
          </label>
          <label>
            Pickup Date Start
            <Input
              type="date"
              name="dateMin"
              defaultValue={brokerFinancialSearchParams.dateMin}
            />
          </label>
          <label>
            Pickup Date End
            <Input
              type="date"
              name="dateMax"
              defaultValue={brokerFinancialSearchParams.dateMax}
            />
          </label>
          <div className={classesCustom.submitHolder}>
            <Button type="submit" variant="good">
              Search
            </Button>

            <Button
              onClick={() => ClearSearchParams()}
              type="button"
              variant="bad"
            >
              Clear
            </Button>
          </div>
        </form>
      </div>
    );
  };

  const Summary = () => {
    const loadCount = tableData?.length;
    const totalRevenue =
      tableData?.reduce(
        (sum, row) => sum + Number(row.revenue.replace(/[^\d.-]/g, '')),
        0,
      ) ?? 0;
    const totalPayroll =
      tableData?.reduce(
        (sum, row) => sum + Number(row.payroll.replace(/[^\d.-]/g, '')),
        0,
      ) ?? 0;
    const totalPayable =
      tableData?.reduce(
        (sum, row) => sum + Number(row.payable.replace(/[^\d.-]/g, '')),
        0,
      ) ?? 0;
    const totalMargin =
      tableData?.reduce(
        (sum, row) => sum + Number(row.margin.replace(/[^\d.-]/g, '')),
        0,
      ) ?? 0;
    const marginPercentage =
      totalRevenue === 0 ? 0 : ((totalMargin / totalRevenue) * 100).toFixed(2);

    return (
      <div className={classesFinancialReport.summaryRow}>
        <div>
          <span className={classesFinancialReport.summaryLabel}>
            Load Count:
          </span>
          {loadCount}
        </div>
        <div>
          <span className={classesFinancialReport.summaryLabel}>
            Total Revenue:
          </span>
          {totalRevenue.toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD',
          })}
        </div>
        <div>
          <span className={classesFinancialReport.summaryLabel}>
            Total Payroll:
          </span>
          {totalPayroll.toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD',
          })}
        </div>
        <div>
          <span className={classesFinancialReport.summaryLabel}>
            Total Payable:
          </span>
          {totalPayable.toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD',
          })}
        </div>
        <div>
          <span className={classesFinancialReport.summaryLabel}>
            Total Margin:
          </span>
          {totalMargin.toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD',
          })}
        </div>
        <div>
          <span className={classesFinancialReport.summaryLabel}>Margin:</span>{' '}
          {marginPercentage}%
        </div>
      </div>
    );
  };

  return (
    <>
      <Header>
        <button
          onClick={() => {
            DownloadReportCSV(tableData);
          }}
          className={classesFinancialReport.downloadButton}
        >
          <MdDownload />
          <span>Download</span>
        </button>
      </Header>
      <SearchForm />
      <Summary />
      {isLoading ? (
        <h3>Generating Report...</h3>
      ) : (
        <BBITable data={tableData} columns={columnConfig} />
      )}
    </>
  );
}

export default BrokerFinancials;
